/**
 * A conversation renamed to the ID the server stored it under, in what the
 * page keeps by conversation (AppSecure's followServerSession).
 *
 * The gateway stores a turn under an ID of its own when the one it was sent
 * is someone else's, or was deleted in another tab or on another device. The
 * page went on sending the old one, and every turn after it reached the model
 * with none of the history.
 */

/**
 * The list with the conversation under its new ID. A copy already listed
 * under that ID (a refresh from the server) gives way to the page's own,
 * which has the turn that is still streaming.
 */
export function renameSessionIn<T extends { id: string }>(sessions: T[], from: string, to: string): T[] {
  if (!sessions.some(session => session.id === from)) return sessions;
  return sessions
    .filter(session => session.id !== to)
    .map(session => (session.id === from ? { ...session, id: to } : session));
}

/** The record with what was kept under `from` kept under `to`. */
export function renameKey<V>(record: Record<string, V>, from: string, to: string): Record<string, V> {
  if (!(from in record)) return record;
  const { [from]: value, ...rest } = record;
  return { ...rest, [to]: value };
}

/** Moves the entry under `from` to `to`, in place. */
export function renameMapKey<V>(map: Map<string, V>, from: string, to: string): void {
  if (!map.has(from)) return;
  map.set(to, map.get(from) as V);
  map.delete(from);
}

/** The set with `to` where it had `from`. */
export function renameInSet(set: Set<string>, from: string, to: string): Set<string> {
  if (!set.has(from)) return set;
  const next = new Set(set);
  next.delete(from);
  next.add(to);
  return next;
}
